/**
 * Standalone Segment User Manager
 * Manages user data and event tracking without Alpine.js dependency
 *
 * Identify-first guarantee: when a userId is known, analytics.identify()
 * always fires before analytics.track()/page() within the same call,
 * eliminating race conditions between identify and other events.
 */

const SegmentUser = {
  traits: {},
  customerTraits: {},
  email: '',
  isInitialized: false,
  _hasIdentified: false,
  pendingEvents: [],
  maxRetries: 50,
  retryDelay: 50,
  productsData: [],

  /**
   * Initialize the user manager
   * Called when analytics SDK is ready
   */
  init() {
    this.isInitialized = true;

    // Pick up customer data set from Liquid before this module loaded
    if (window.__pvolveCustomer) {
      const { email, traits } = window.__pvolveCustomer;
      if (email) {
        this.setEmail(email);
        this.customerTraits = { ...this.customerTraits, ...traits };
      }
      delete window.__pvolveCustomer;
    }

    console.log('[SegmentUser] Initialized', this.email ? `(user: ${this.email})` : '(anonymous)');
    this.processPendingEvents();
    // Start IP address fetch in background
    this.setIpAddress();
  },

  /**
   * Store customer data from Shopify Liquid (called before analytics loads)
   * This avoids needing a separate retry loop for identify.
   * @param {String} email - Customer email
   * @param {Object} traits - Customer traits from Shopify
   */
  setCustomerData(email, traits = {}) {
    if (email) {
      this.setEmail(email);
      this.customerTraits = { ...this.customerTraits, ...traits };
    }
  },

  /**
   * Get cookie value by name
   * @param {String} name - Cookie name
   * @returns {String} Cookie value
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  },

  /**
   * Get user ID from email or localStorage
   * @returns {String} User ID (email)
   */
  async getUserId() {
    try {
      let userId = this.email;
      if (!userId) {
        userId = window.localStorage.getItem('ajs_user_id') || '';
      }
      return userId;
    } catch (e) {
      console.error('[SegmentUser] Error getting user ID:', e);
      return '';
    }
  },

  /**
   * Fetch and cache IP address from Lightbox CDN
   * @returns {String} IP address
   */
  async setIpAddress() {
    // Check cache first
    const cachedIp = window.sessionStorage.getItem('ipAddress');
    if (cachedIp) {
      try {
        return JSON.parse(cachedIp);
      } catch (e) {}
    }
    // Fetch IP in background
    try {
      const response = await fetch('https://api.lightboxcdn.com/');
      const data = await response.json();
      window.sessionStorage.setItem('ipAddress', JSON.stringify(data.geo.ip));
      return data.geo.ip;
    } catch (e) {
      return '';
    }
  },

  /**
   * Get IP address synchronously (non-blocking)
   * @returns {String} Cached IP or empty string
   */
  getIpAddressSync() {
    const cachedIp = window.sessionStorage.getItem('ipAddress');
    if (cachedIp) {
      try {
        return JSON.parse(cachedIp);
      } catch (e) {}
    }
    // Start background fetch but don't wait
    this.setIpAddress();
    return '';
  },

  /**
   * Send a Segment event with identify-first guarantee.
   *
   * When `traits` is non-null (even `{}`) and there is a known userId,
   * analytics.identify() fires BEFORE analytics.track()/page().
   * Stored customerTraits are merged into every identify call.
   *
   * @param {Object|null} traits - User traits to identify (pass {} for auto-identify, null to skip)
   * @param {Object|null} eventData - Event data with eventName and properties
   */
  async sendSegmentEvent(traits, eventData) {
    // Snapshot page context synchronously before any async work,
    // so navigation between awaits cannot corrupt context.page
    const pageContext = {
      path: window.location.pathname,
      url: window.location.href,
      search: window.location.search,
      title: document.title,
      referrer: document.referrer,
    };

    if (typeof analytics === 'object') {
      try {
        // Get Facebook pixel cookies
        const fbpCookie = this.getCookie('_fbp') || '';
        const fbcCookie = this.getCookie('_fbc') || '';
        const segmentAnonId = window.localStorage.getItem('ajs_anonymous_id') || '';
        const userId = await this.getUserId();

        // Get IP address (non-blocking for page views)
        const isPageView = eventData != null && eventData.is_page_view;
        const ip = isPageView ? this.getIpAddressSync() : await this.setIpAddress();

        // Identify-first: when traits is non-null and we have a userId,
        // always call identify before any track/page (Skyline pattern)
        if (traits !== null && traits !== undefined && userId) {
          const mergedTraits = {
            ...this.customerTraits,
            ...traits,
            sent_from: 'Pvolve Web Script',
            fbp: fbpCookie,
            fbc: fbcCookie,
            anonymousId: segmentAnonId,
            ip: ip,
          };
          // Email goes as userId, not as a trait
          delete mergedTraits.email;
          analytics.identify(userId, mergedTraits);
          this._hasIdentified = true;
        }

        // Track event if data provided
        if (eventData && eventData.eventName) {
          const { eventName, ...properties } = eventData;
          const enrichedProperties = {
            ...properties,
            sent_from: 'Pvolve Web Script',
            fbp: fbpCookie,
            fbc: fbcCookie,
            userId: userId,
            anonymousId: segmentAnonId,
            ip: ip,
            email: userId,
          };

          // Pass snapshotted page context so the SDK uses the correct page
          // even if window.location changed during the async gap
          const contextOverride = { context: { page: pageContext } };

          // Use analytics.page() for page views, analytics.track() for other events
          if (isPageView) {
            delete enrichedProperties.is_page_view;
            analytics.page(eventName, enrichedProperties, contextOverride);
          } else {
            analytics.track(eventName, enrichedProperties, contextOverride);
          }
          console.log('[SegmentUser] Event tracked:', eventName);
        }
      } catch (err) {
        console.error('[SegmentUser] Error sending event:', err);
      }
    } else {
      // Queue event if analytics not ready
      this.pendingEvents.push({ traits, eventData });
      console.warn('[SegmentUser] Analytics not ready, event queued:', eventData?.eventName);
    }
  },

  /**
   * Send a product clicked event
   * @param {String} productHandle - The product handle/slug
   */
  sendProductClickedEvent(productHandle) {
    if (!productHandle) return;

    // Defensive check: ensure productsData is an array
    if (!Array.isArray(this.productsData)) {
      this.productsData = [];
    }

    const foundProduct = this.productsData.find((product) => product.handle === productHandle);

    if (foundProduct) {
      // Send enriched product data if found
      const formattedPrice = foundProduct.price / 100;
      this.sendSegmentEvent(null, {
        eventName: 'Product Clicked',
        sent_from: 'Pvolve Web Script',
        brand: foundProduct.vendor,
        category: foundProduct.product_type,
        url: window.location.href + '/products/' + foundProduct.handle,
        product_id: foundProduct.id,
        sku: foundProduct.variants[0]?.sku || '',
        name: foundProduct.title,
        price: formattedPrice,
        variant: foundProduct.variants[0]?.title || '',
        shopify_product_id: foundProduct.id,
        shopify_variant_id: foundProduct.variants[0]?.id || '',
        currency: foundProduct.currency || 'USD',
        image_url: foundProduct.featured_image,
        tags: foundProduct.tags,
        compare_at_price: foundProduct.compare_at_price,
      });
    } else {
      // Send basic event if product data not available
      this.sendSegmentEvent(null, {
        eventName: 'Product Clicked',
        product_handle: productHandle,
        sent_from: 'Pvolve Web Script',
      });
    }
  },

  /**
   * Set products data for enriched product click events
   * @param {Array} products - Array of product objects
   */
  setProductsData(products) {
    if (Array.isArray(products)) {
      this.productsData = products;
    } else if (typeof products === 'string') {
      try {
        this.productsData = JSON.parse(products);
      } catch (e) {
        this.productsData = [];
      }
    } else {
      this.productsData = [];
    }
  },

  /**
   * Set user email
   * @param {String} email - User email
   */
  setEmail(email) {
    if (email) {
      this.email = email.toLowerCase();
      this.traits.email = this.email;
    }
  },

  /**
   * Identify user with email and traits.
   * Stores traits and fires identify immediately if analytics is ready.
   * @param {String} email - User email
   * @param {Object} additionalTraits - Additional user traits
   */
  identifyUser(email, additionalTraits = {}) {
    if (!email) return;

    this.setEmail(email);
    this.customerTraits = { ...this.customerTraits, ...additionalTraits };

    // Fire identify immediately (sendSegmentEvent handles queuing if SDK not ready)
    this.sendSegmentEvent(additionalTraits, null);
    console.log('[SegmentUser] User identified:', email);
  },

  /**
   * Get stored user email
   * @returns {String} User email
   */
  getEmail() {
    return this.email;
  },

  /**
   * Get stored user traits
   * @returns {Object} User traits
   */
  getTraits() {
    return this.traits;
  },

  /**
   * Process all pending events once analytics is ready
   */
  async processPendingEvents() {
    if (this.pendingEvents.length === 0) return;

    console.log(`[SegmentUser] Processing ${this.pendingEvents.length} pending events`);

    for (const { traits, eventData } of this.pendingEvents) {
      await this.sendSegmentEvent(traits, eventData);
    }

    this.pendingEvents = [];
  },

  /**
   * Check if analytics SDK is loaded and initialize if ready
   * Uses exponential backoff for retries
   * @param {Number} retryCount - Current retry attempt
   */
  checkAndInitialize(retryCount = 0) {
    if (typeof analytics === 'object') {
      this.init();
    } else if (retryCount < this.maxRetries) {
      const delay = Math.min(this.retryDelay + (retryCount * 25), 200);
      setTimeout(() => this.checkAndInitialize(retryCount + 1), delay);
    } else {
      console.error('[SegmentUser] Analytics SDK failed to load after maximum retries');
    }
  },
};

// Start checking for analytics availability
SegmentUser.checkAndInitialize();

// Make globally accessible
window.SegmentUser = SegmentUser;

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SegmentUser;
}
